import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { all, takeLatest, delay, call, select, put, race, take, takeEvery } from 'redux-saga/effects';
import { withCursor, streamActions } from '../../Stream';
import { watchedGroupsStreamId, recommendedGroupsStreamId } from './constants';
import { fetchMoreWatchedGroups, fetchMoreRecommendedGroups, initialFetch, unrecommendGroup, togglePinGroup, toggleWatchGroup, initializeNetworkBar, markChannelAsRead, updateNetworkBarSection, setNetworkBarSection } from './actions';
import { normalize } from 'normalizr';
import { userListSchema, userSchema } from '../../redux/normalizr/schemas/user';
import { fetchGroups, doPinGroup, doUnpinGroup, doUnrecommendGroup, doToggleSection } from './xhr';
import { addNotification } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications';
import { getIsNetworkBarInitialized } from './selectors';
import { getDataEntityById } from '../../redux/entities';
import { putEntities } from '../../redux/entities/actions';
import { groupLeaveSuccess, groupJoinSuccess } from '../../redux/groups/actions';
import { FetchType } from '../../Stream/base/types';
function* processStreams(initData, content_type) {
    const fetchWatched = content_type !== 'recommended_groups';
    const fetchRecommended = content_type !== 'watched_groups';
    if (fetchWatched) {
        var _initData_watchedGroups;
        if (!initData || ((_initData_watchedGroups = initData.watchedGroups) === null || _initData_watchedGroups === void 0 ? void 0 : _initData_watchedGroups.errorCode)) {
            yield put(withCursor.actions.fetchFailure({
                streamId: watchedGroupsStreamId,
                fetchType: FetchType.Next,
                errorMsg: 'Failed to fetch groups'
            }));
        } else {
            var _initData_watchedGroups1, _initData_watchedGroups2, _initData_watchedGroups3, _initData_watchedGroups4;
            var _initData_watchedGroups_groups;
            const { result: items, entities } = normalize((_initData_watchedGroups_groups = (_initData_watchedGroups1 = initData.watchedGroups) === null || _initData_watchedGroups1 === void 0 ? void 0 : _initData_watchedGroups1.groups) !== null && _initData_watchedGroups_groups !== void 0 ? _initData_watchedGroups_groups : [], userListSchema);
            yield all([
                put(withCursor.actions.fetchSuccess({
                    streamId: watchedGroupsStreamId,
                    items,
                    entities,
                    hasMore: !!((_initData_watchedGroups2 = initData.watchedGroups) === null || _initData_watchedGroups2 === void 0 ? void 0 : _initData_watchedGroups2.hasMore),
                    cursor: (_initData_watchedGroups3 = initData.watchedGroups) === null || _initData_watchedGroups3 === void 0 ? void 0 : _initData_watchedGroups3.cursor
                })),
                put(setNetworkBarSection('watched_groups', !!((_initData_watchedGroups4 = initData.watchedGroups) === null || _initData_watchedGroups4 === void 0 ? void 0 : _initData_watchedGroups4.isCollapsed)))
            ]);
        }
    }
    if (fetchRecommended) {
        var _initData_recommendedGroups;
        if (!initData || ((_initData_recommendedGroups = initData.recommendedGroups) === null || _initData_recommendedGroups === void 0 ? void 0 : _initData_recommendedGroups.errorCode)) {
            yield put(withCursor.actions.fetchFailure({
                streamId: recommendedGroupsStreamId,
                fetchType: FetchType.Next,
                errorMsg: 'Failed to fetch recommended groups'
            }));
        } else {
            var _initData_recommendedGroups1, _initData_recommendedGroups2, _initData_recommendedGroups3;
            var _initData_recommendedGroups_groups;
            const { result: items, entities } = normalize((_initData_recommendedGroups_groups = (_initData_recommendedGroups1 = initData.recommendedGroups) === null || _initData_recommendedGroups1 === void 0 ? void 0 : _initData_recommendedGroups1.groups) !== null && _initData_recommendedGroups_groups !== void 0 ? _initData_recommendedGroups_groups : [], userListSchema);
            yield all([
                put(withCursor.actions.fetchSuccess({
                    streamId: recommendedGroupsStreamId,
                    items,
                    entities,
                    hasMore: false,
                    cursor: (_initData_recommendedGroups2 = initData.recommendedGroups) === null || _initData_recommendedGroups2 === void 0 ? void 0 : _initData_recommendedGroups2.cursor
                })),
                put(setNetworkBarSection('recommended_groups', !!((_initData_recommendedGroups3 = initData.recommendedGroups) === null || _initData_recommendedGroups3 === void 0 ? void 0 : _initData_recommendedGroups3.isCollapsed)))
            ]);
        }
    }
}
function* loadNetworkbarData(content_type, cursor) {
    const result = yield call(fetchGroups, content_type, cursor);
    return result;
}
function* resetStream(content_type) {
    // we are not going to fire actual stream fetch actions here
    // so we can combine all the fetches into one when possible
    if (!content_type || content_type === 'watched_groups') {
        yield put(withCursor.actions.initialize({
            streamId: watchedGroupsStreamId,
            itemsPerFetch: 50,
            hasMore: true,
            fetchNextCallback: fetchMoreWatchedGroups
        }));
        yield put(streamActions.markAsFetching(watchedGroupsStreamId));
    }
    if (!content_type || content_type === 'recommended_groups') {
        yield put(withCursor.actions.initialize({
            streamId: recommendedGroupsStreamId,
            itemsPerFetch: 3,
            hasMore: true,
            fetchNextCallback: fetchMoreRecommendedGroups
        }));
        yield put(streamActions.markAsFetching(recommendedGroupsStreamId));
    }
}
function* handleFetchMoreWatchedGroups({ payload: { streamId } }) {
    yield delay(200);
    const cursor = yield select(withCursor.selectors.getCursor, streamId);
    const groups = yield call(loadNetworkbarData, 'watched_groups', cursor);
    yield call(processStreams, groups, 'watched_groups');
}
function* handleFetchMoreRecommendedGroups({ payload: { streamId } }) {
    yield delay(200);
    const cursor = yield select(withCursor.selectors.getCursor, streamId);
    const groups = yield call(loadNetworkbarData, 'recommended_groups', cursor);
    yield call(processStreams, groups, 'recommended_groups');
}
function* handleTogglePinGroup({ payload: { groupId, isPinnedAfter } }) {
    const result = yield call(isPinnedAfter ? doPinGroup : doUnpinGroup, groupId);
    if (!result || !result.success) {
        yield put(addNotification(NotificationType.Generic, {
            isError: true,
            messageLocaleKey: isPinnedAfter ? 'networkBar.errors.failedToPin' : 'networkBar.errors.failedToUnpin'
        }));
        return;
    }
    yield call(resetStream, 'watched_groups');
    const groups = yield call(loadNetworkbarData, 'watched_groups');
    yield call(processStreams, groups, 'watched_groups');
}
function* handleUnrecommendGroup({ payload: { groupId } }) {
    const result = yield call(doUnrecommendGroup, groupId);
    if (!result || !result.success) {
        yield put(addNotification(NotificationType.Generic, {
            isError: true,
            messageLocaleKey: 'networkBar.errors.failedToUnrecommend'
        }));
        return;
    }
    yield call(resetStream, 'recommended_groups');
    const groups = yield call(loadNetworkbarData, 'recommended_groups');
    yield call(processStreams, groups, 'recommended_groups');
}
function* handleToggleWatchGroup({ payload: { isWatchingAfter } }) {
    // if watching, reset both streams, if unwatching, just the watched one is enough
    const contentType = isWatchingAfter ? undefined : 'watched_groups';
    yield call(resetStream, contentType);
    const groups = yield call(loadNetworkbarData, contentType);
    yield call(processStreams, groups, contentType);
}
function* handleGroupMembershipChange() {
    yield call(resetStream);
    const groups = yield call(loadNetworkbarData);
    yield call(processStreams, groups);
}
function* handleMarkChannelAsRead({ payload: { channel } }) {
    var _group_networkbar;
    // main navigation is handled by reducer, we take care of groups here
    const group = yield select(getDataEntityById, userSchema, channel);
    if (!(group === null || group === void 0 ? void 0 : (_group_networkbar = group.networkbar) === null || _group_networkbar === void 0 ? void 0 : _group_networkbar.hasNewContent)) {
        return;
    }
    yield put(putEntities({
        entities: {
            [userSchema.key]: {
                [group.userId]: _object_spread_props(_object_spread({}, group), {
                    networkbar: _object_spread_props(_object_spread({}, group.networkbar), {
                        hasNewContent: false
                    })
                })
            }
        }
    }));
}
function* handleToggleNetworkBarSection({ payload: { section, isFoldedNow } }) {
    // fire and forget, it's not critical
    yield call(doToggleSection, section, isFoldedNow);
}
export default function* saga() {
    const isInitialized = yield select(getIsNetworkBarInitialized);
    if (!isInitialized) {
        yield call(resetStream);
        // we either init on component mount or init action from ssr, whichever comes first
        const { needFetchFirst, alreadyFetched } = yield race({
            needFetchFirst: take(initialFetch),
            alreadyFetched: take(initializeNetworkBar)
        });
        if (needFetchFirst) {
            const initData = yield call(loadNetworkbarData);
            if (initData) {
                yield call(processStreams, initData);
            }
            yield put(initializeNetworkBar(initData || {})); // init menu in store
        }
        if (alreadyFetched) {
            yield call(processStreams, alreadyFetched.payload);
        }
    }
    yield all([
        takeLatest(fetchMoreWatchedGroups, handleFetchMoreWatchedGroups),
        takeLatest(fetchMoreRecommendedGroups, handleFetchMoreRecommendedGroups),
        takeLatest(togglePinGroup, handleTogglePinGroup),
        takeLatest(toggleWatchGroup, handleToggleWatchGroup),
        takeLatest(unrecommendGroup, handleUnrecommendGroup),
        takeEvery(markChannelAsRead, handleMarkChannelAsRead),
        takeLatest(updateNetworkBarSection, handleToggleNetworkBarSection),
        takeEvery([
            groupJoinSuccess,
            groupLeaveSuccess
        ], handleGroupMembershipChange)
    ]);
}
